import { useEffect, useId, useRef, useState } from "react";
import { useStudioStore } from "@/store/studio-store";
import { toast } from "@/store/toast-store";
import { handleProjectCreateError } from "@/store/project-create-error";
import { ArrowUp, LoaderCircle, Paperclip, X } from "lucide-react";
import { composerActionState } from "../composer-action-state";
import {
  StudioGenerationBusyError,
  deriveAsIsSnapshotFromPrompt,
  startAppGenerationFromPrompt,
  type LibraryStructureCandidate,
  type LibraryStructureSelectionChoice,
  type StudioOutcomeIntent,
} from "@/features/chat/services/start-app-generation-from-prompt";
import { mintClassifierDispatchTrigger } from "@/features/chat/services/studio-creation-router";
import { tryNativeUiBlockStudioGeneration } from "@/features/chat/services/native-ui-block-studio-generation";
import {
  planningEvidenceFromMetadata,
  planningEvidenceFromProgress,
  type PlanningEvidence,
} from "@/features/project/lib/planning-evidence";
import {
  buildLandingStudioNotice,
  consumeGenericLandingStudioHandoff,
} from "@/lib/landing-studio-handoff";
import { useStudioLocale } from "@/i18n";
import {
  TemplateProposalExchange,
  type TemplateProposalContent,
} from "@/features/templates/components/template-proposal-exchange";
import {
  claimStudioGenerationRequest,
  releaseStudioGenerationRequest,
} from '@/features/productization/studio-featured-outcome-draft'
import { findStudioOutcomeCatalogEntry } from '@/features/productization/studio-outcome-catalog'
import {
  StudioApplicationOntologyGenerationRefusal,
  readPersistedApplicationOntologyGenerationSelection,
} from '@/features/application-ontology/application-ontology-generation'
import { mapCreatorPlanningFailureMessage } from '../planning-failure-copy'
import { isComposerAtRest } from './composer-rest-state'
import { getCoogoStudioHomeRenewalCopy } from '../coogo-studio-home-renewal-copy'
import { isOpenPencilFigmaImportAvailable } from '@/features/openpencil-figma/openpencil-figma-client'
import { getLibraryStructureCardsCopy } from '../library-structure-cards-copy'
import { routeStudioCreationRequest } from '@/features/chat/services/studio-creation-router'
import { checkTermsAgainstDictionary } from '@/features/chat/services/reference-term-dictionary'
import type { ContractReadback } from '@/features/chat/services/contract-readback'
import {
  formatFieldVisibilityDecision,
  type FieldSpecReadback,
} from '@/features/chat/services/field-spec-readback'
import {
  formatLegacyConversionDecision,
  type LegacyScreenReadback,
  type LegacyTraitKey,
} from '@/features/chat/services/legacy-screen-readback'
import {
  formatScreenDesignDecision,
  type ScreenDesignReadback,
} from '@/features/chat/services/screen-design-readback'
import {
  formatReferenceExtractionDecision,
  type ReferenceExtractionReadback,
} from '@/features/chat/services/reference-extraction-readback'
import {
  classifyDroppedSourceFile,
  stagePendingSourceDrop,
  DROPPABLE_EXTENSIONS,
} from '@/features/source-document/pending-source-drop'
import { getSourceDropCopy } from '../source-drop-copy'

/** G3 — pending U2 judgment surfaced as inline cards (clarification-style). */
interface LibraryStructureJudgment {
  candidates: LibraryStructureCandidate[]
  resolve: (choice: LibraryStructureSelectionChoice) => void
}

interface HeroChatInputProps {
  onTemplateSelect?: () => void
  onSourceSelect?: () => void
  showGreeting?: boolean
  className?: string
  homeState?: 'first' | 'returning'
  prefillRequest?: string | null
  /**
   * W21 rung ② — a document carried from the chat thread composer through
   * the intake-carry channel. Applied ONCE: routed through the same
   * submit path, which deterministically opens the matching read-back
   * card(s); a non-intake text only prefills and never auto-creates.
   */
  intakeCarryPrompt?: string | null
  outcomeIntent?: StudioOutcomeIntent | null
  outcomeDraftId?: string | null
  outcomeAwaitingClarification?: boolean
  onOutcomeIntentConsumed?: (draftId: string) => void
  onOutcomeDraftChange?: (draftId: string, prompt: string, awaitingClarification: boolean) => void
  /**
   * Fires whenever this composer becomes — or stops being — AT REST.
   *
   * At rest means it is holding nothing the creator still needs: no unsent
   * text, no run in flight, and none of its own surfaces on screen. The
   * parent may hand the screen to the chat dock only while this is true; see
   * `composer-rest-state.ts` for the rule and for the measured defect that
   * replaced its predecessor (`onPlanningAlertChange`, which reported one
   * enumerated state and left every other surface inside a hidden container).
   */
  onRestStateChange?: (atRest: boolean) => void
  ariaDescribedBy?: string
}

function requestIdentity(
  outcomeDraftId: string | null,
  prefillRequest: string | null,
): string {
  if (outcomeDraftId) return `draft:${outcomeDraftId}`;
  return prefillRequest ? `generic:prefill:${prefillRequest}` : "generic:free";
}

/**
 * Task 5 (as-is -> to-be conversion UX) — true when `startMode` is
 * 'conversion' but the prompt carries no original a screen could actually be
 * derived from.
 *
 * The judgment reuses `deriveAsIsSnapshotFromPrompt` — the SAME function
 * `startAppGenerationFromPrompt` calls to decide whether `settings.asIsSnapshot`
 * gets persisted (Task 1) — rather than a separate heuristic (e.g. a regex
 * for `<form`). That way the notice and the actual generation outcome can
 * never disagree: if this returns true, no as-is snapshot will be saved
 * either. `capturedAt` is a fixed placeholder — this call only ever inspects
 * the null/non-null verdict, never the returned snapshot's timestamp.
 */
export function shouldNoticeConversionWithoutOriginal(
  startMode: 'new' | 'conversion',
  prompt: string,
): boolean {
  if (startMode !== 'conversion') return false
  return deriveAsIsSnapshotFromPrompt(prompt, '1970-01-01T00:00:00.000Z') === null
}

export function HeroChatInput({
  onSourceSelect,
  showGreeting = true,
  className = '',
  homeState = 'returning',
  prefillRequest = null,
  intakeCarryPrompt = null,
  outcomeIntent = null,
  outcomeDraftId = null,
  outcomeAwaitingClarification = false,
  onOutcomeIntentConsumed,
  onOutcomeDraftChange,
  onRestStateChange,
  ariaDescribedBy,
}: HeroChatInputProps) {
  const { locale, strings } = useStudioLocale()
  const t = strings.chat
  const outcomeCopy = strings.home.featuredOutcome
  const selectedOutcomeEntry = outcomeIntent
    ? findStudioOutcomeCatalogEntry(outcomeIntent.skuId)
    : null
  const selectedOutcomeCopy = selectedOutcomeEntry
    ? outcomeCopy.outcomes[selectedOutcomeEntry.copyKey]
    : null
  const renewalCopy = getCoogoStudioHomeRenewalCopy(locale)
  const libraryCopy = getLibraryStructureCardsCopy(locale)
  const dropCopy = getSourceDropCopy(locale)
  const figmaImportAvailable = isOpenPencilFigmaImportAvailable()
  const availableSourceExtensions = figmaImportAvailable
    ? DROPPABLE_EXTENSIONS
    : DROPPABLE_EXTENSIONS.filter(
        (extension) => extension !== '.fig' && extension !== '.pen',
      )
  const generatedDescriptionId = useId()
  const composerDescriptionId = ariaDescribedBy ?? generatedDescriptionId
  const sourcePickerRef = useRef<HTMLInputElement>(null)
  const [prompt, setPrompt] = useState('')
  // 시작-방식. 대화 상태일 뿐 영속되지 않는다 — "전환이다"의 진실은
  // 프로젝트에 남은 as-is 스냅샷이지 이 값이 아니다.
  const [startMode, setStartMode] = useState<'new' | 'conversion'>('new')
  // G3 — while non-null, the composer shows the U2 library structure cards
  // and the generation flow is awaiting `resolve` (same judgment grammar as
  // the clarification box; no modal, no auto-opened chat).
  const [libraryStructureJudgment, setLibraryStructureJudgment] =
    useState<LibraryStructureJudgment | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const composerAction = composerActionState({
    running: isCreating,
    hasPrompt: prompt.trim().length > 0,
  })

  const [isDragActive, setIsDragActive] = useState(false)
  // A drop this composer cannot forward. Rendered next to the input, because a
  // navigation-only handler previously made an unsupported file look accepted.
  const [dropRejection, setDropRejection] = useState<{
    fileName: string | null
    extension: string | null
  } | null>(null)
  const [landingHandoffPrompt, setLandingHandoffPrompt] = useState<string | null>(null)
  // Task 5 — "전환" is a promise, not a decoration. When the submitted text
  // carries no original a screen could actually be derived from, this fires
  // right at submit time (before generation runs) so the creator learns the
  // run is proceeding as new rather than discovering it only after the wait.
  // Dismissible like `dropRejection`, since it is about one submission, not
  // an ongoing state.
  const [conversionWithoutOriginalNotice, setConversionWithoutOriginalNotice] =
    useState(false)
  // S1 (WonBanking scenarios doc) — a pasted contract is answered with a
  // deterministic read-back card, not generation. While non-null the card is
  // showing and the prompt stays in the composer; confirm re-submits the same
  // text with `confirmedIntakeRef` armed so the router's decision is
  // honoured exactly once.
  const [contractCard, setContractCard] = useState<{
    readback: ContractReadback
    prompt: string
  } | null>(null)
  // S2 — a pasted message-spec field table gets a checkbox pass: which fields
  // appear on screen, which travel internally. `visible` is index-aligned
  // with readback.fields; everything starts visible and unchecking is the
  // deliberate act of marking a field internal.
  const [fieldSpecCard, setFieldSpecCard] = useState<{
    readback: FieldSpecReadback
    prompt: string
    visible: boolean[]
  } | null>(null)
  // S3 — a pasted legacy screen source is a conversion, not a new build. The
  // card compares what was read with what conversion does, and `rulings`
  // holds the human's per-trait carry-vs-improve decision. Faithful
  // conversion ('keep') is the baseline; 'improve' is the deliberate act.
  const [legacyCard, setLegacyCard] = useState<{
    readback: LegacyScreenReadback
    prompt: string
    rulings: Partial<Record<LegacyTraitKey, 'keep' | 'improve'>>
  } | null>(null)
  // S4 — a pasted design document decides the layout and the quoted labels,
  // and usually not the data. The card states that gap; proceeding
  // screen-first is an explicit decision, never an oversight.
  const [designCard, setDesignCard] = useState<{
    readback: ScreenDesignReadback
    prompt: string
  } | null>(null)
  // S7 — "make it like this" extracts the standard, never clones. Structure
  // carries by default; each term carries ONLY when the human marks it
  // standard (`termCarried`, index-aligned with readback.terms) — the
  // anti-clone default the scenarios doc asks for.
  const [referenceCard, setReferenceCard] = useState<{
    readback: ReferenceExtractionReadback
    prompt: string
    structureCarried: boolean
    termCarried: boolean[]
  } | null>(null)
  // A ready-made template that matches what was just typed. Home shows this
  // ITSELF now.
  //
  // It used to be handed to the chat rail — `appendChatMessage` + clear the
  // prompt + open the rail + stay on Home — and the only renderer for that
  // message kind lived inside the rail's message switch. Home hosts its own
  // conversation and the shell adds no rail here, so that handoff would have
  // cleared the person's prompt and shown them nothing. Same `null`-while-
  // absent shape as the intake cards above; the card itself is the shared
  // `TemplateProposalExchange`, which the rail renders too.
  const [templateProposal, setTemplateProposal] =
    useState<TemplateProposalContent | null>(null)
  // Shared by every intake card (S1 contract, S2 field table, S3 legacy
  // screen, S4 design document, S7 reference extraction): `prompt` holds the
  // one exact composite text a confirm click has authorized, so the
  // router's read-back decision is honoured once and any edit is read back
  // again. `originalScreenMarkup` -- fix-round-2 (Task 3) -- is the RAW
  // pre-augmentation segment of whichever card carries a pasted ORIGINAL
  // SCREEN, set in `confirmIntakeCards` alongside the join that builds
  // `prompt` (never derived separately -- round 1 gated this on `legacyCard`
  // alone, which reproduced the identical "our own prose ends up in 원본"
  // bug on the S7 reference lane, since the router deliberately routes a
  // referenced screen there instead of S3).
  const confirmedIntakeRef = useRef<{
    prompt: string
    originalScreenMarkup?: string
  } | null>(null)
  const [clarificationQuestions, setClarificationQuestions] = useState<string[]>([])
  const [awaitingClarification, setAwaitingClarification] = useState(outcomeAwaitingClarification)
  const [planningMessage, setPlanningMessage] = useState<string | null>(null)
  // A1 — creator-facing inline alert shown in place of the progress box when
  // generation fails. `toast.error` alone is not sufficient: the dashboard
  // route has no mounted <Toaster/> in some hosts, so a rejection could
  // otherwise leave the composer looking silently stuck. This state always
  // wins over `planningMessage` in the render below.
  const [planningError, setPlanningError] = useState<string | null>(null)
  const [planningEvidence, setPlanningEvidence] = useState<PlanningEvidence | null>(null)
  /**
   * Every surface this composer puts on screen on top of its resting chrome
   * (the greeting, the start-mode chips, the input shell and its bottom bar).
   *
   * This registry is NOT a list of "which notices are important enough to
   * keep the composer visible" — that shape is the bug it replaced. The two
   * predecessors of this line each enumerated one more special case
   * (2026-08-06: `planningError`; 2026-08-08: the intake cards) and every
   * surface outside the list kept rendering inside the parent's `hidden`
   * container: measured 2026-08-13, `hero-conversion-without-original`,
   * `hero-landing-handoff`, `hero-chat-planning-progress` and
   * `hero-chat-planning-evidence` all had zero client rects on 100% of
   * samples while `hero-chat-planning-error` had one.
   *
   * Membership asks only "does the composer show this?". Anything here — plus
   * unsent text and a run in flight — keeps the composer on screen; nothing
   * else about it matters. `composer-rest-state.test.ts` fails if a `useState`
   * is added to this component that is in neither this registry nor its
   * resting-chrome list, so the next surface cannot be added silently.
   */
  const composerSurfaces = {
    landingHandoffPrompt,
    dropRejection,
    conversionWithoutOriginalNotice,
    contractCard,
    fieldSpecCard,
    legacyCard,
    designCard,
    referenceCard,
    templateProposal,
    clarificationQuestions,
    libraryStructureJudgment,
    planningMessage,
    planningError,
    planningEvidence,
  }
  const composerAtRest = isComposerAtRest({
    unsentText: prompt,
    running: isCreating,
    surfaces: Object.values(composerSurfaces),
  })
  const onRestStateChangeRef = useRef(onRestStateChange)
  onRestStateChangeRef.current = onRestStateChange
  useEffect(() => {
    onRestStateChangeRef.current?.(composerAtRest)
  }, [composerAtRest])
  // W21 rung ② — consume a carried intake document exactly once.
  const intakeCarryAppliedRef = useRef(false)
  useEffect(() => {
    if (!intakeCarryPrompt || intakeCarryAppliedRef.current) return
    intakeCarryAppliedRef.current = true
    setPrompt(intakeCarryPrompt)
    const decision = routeStudioCreationRequest(intakeCarryPrompt)
    if (
      decision.kind === 'zero-base-generation' ||
      decision.kind === 'template-proposal'
    ) {
      // Not an intake document after all (the carry writer re-validates,
      // so this is belt-and-braces) — prefill only, never auto-create.
      return
    }
    void handleSubmit(intakeCarryPrompt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeCarryPrompt])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const submitLock = useRef(false)
  const abortRef = useRef<AbortController | null>(null)
  const activeRequestIdentityRef = useRef<string | null>(null)
  const latestRequestIdentityRef = useRef(requestIdentity(outcomeDraftId, prefillRequest))
  const requestSequenceRef = useRef(0)
  const landingHandoffAppliedRef = useRef(false)
  const user = useStudioStore((s) => s.user)
  const conversationDesignSystemPreset = useStudioStore((s) => s.conversation.designSystemPreset)
  const setChatSidebarOpen = useStudioStore((s) => s.setChatSidebarOpen)
  const setInspectorPanelOpen = useStudioStore((s) => s.setInspectorPanelOpen)
  const setPendingChatNotice = useStudioStore((s) => s.setPendingChatNotice)
  const displayName = user?.user_metadata?.full_name ?? user?.email?.split('@')[0] ?? 'Creator'
  latestRequestIdentityRef.current = requestIdentity(outcomeDraftId, prefillRequest)

  const activeDesignSystemPreset = conversationDesignSystemPreset;

  // Auto-resize textarea. scrollHeight can under-measure while the element is
  // not fully laid out (observed live: height stuck at 26px, slicing the
  // placeholder glyphs mid-line — design-review FINDING-002 2026-07-27), so
  // the className also carries a two-line mobile floor (74px = pt-[18px] +
  // two leading-6 lines + pb-2) and the existing 50px desktop floor. CSS
  // min-height wins over this inline height; the 120px cap below is unchanged.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [prompt]);

  useEffect(() => {
    if (landingHandoffAppliedRef.current) return
    const handoff = consumeGenericLandingStudioHandoff()
    if (!handoff) return
    landingHandoffAppliedRef.current = true
    setLandingHandoffPrompt(handoff.prompt)
    setPrompt(handoff.prompt)
    setPendingChatNotice(buildLandingStudioNotice(handoff))
    setInspectorPanelOpen(false)
    window.requestAnimationFrame?.(() => textareaRef.current?.focus())
  }, [setChatSidebarOpen, setInspectorPanelOpen, setPendingChatNotice])

  // The banner says "your earlier request is already in this conversation" —
  // a claim about text that is PRESENT. Once the box is empty the claim is
  // false, and the banner has to go with it.
  //
  // Measured regression (review I-1): it was set once and never cleared, and
  // it is a member of `composerSurfaces`, so `isComposerAtRest` was false
  // forever — every visitor arriving from cogo.xyz got a second, larger input
  // permanently shadowing the dock conversation, with no control that
  // released it. Exactly the behaviour the rest rule is supposed to preserve.
  // Tying the banner's lifetime to the text it describes is what makes the
  // surface honest AND lets the composer reach rest again; a dismiss button
  // would have done neither, since the stale claim would still be true-looking
  // until clicked.
  // EDGE-2 (review): gating on "the box is empty" was the wrong reading of my
  // own criterion. Select-all-and-retype — the ordinary way a creator rewrites
  // a prefilled box — never empties it, so the banner went on asserting that
  // the carried request was in this conversation while the box held something
  // else. The claim is about THIS TEXT, so the gate is divergence, not
  // emptiness.
  useEffect(() => {
    if (landingHandoffPrompt !== null && prompt !== landingHandoffPrompt) {
      setLandingHandoffPrompt(null)
    }
  }, [landingHandoffPrompt, prompt])

  // Same class as I-1, decided (review minor): the clarification questions are
  // ABOUT a submitted prompt. They are cleared on the next submit, but an
  // empty box cannot be submitted — so abandoning the flow left them mounted
  // with no path to clear, and the composer could never reach rest again.
  //
  // Gated on EMPTY, deliberately NOT on divergence like the banner above: a
  // creator editing the box is usually answering the questions, and clearing
  // them mid-answer would delete the thing they are responding to. Emptying
  // the box is the abandonment signal; editing it is not.
  useEffect(() => {
    if (clarificationQuestions.length > 0 && prompt.trim().length === 0) {
      setClarificationQuestions([])
    }
  }, [clarificationQuestions, prompt])

  useEffect(() => {
    if (!prefillRequest) return
    setClarificationQuestions([])
    setAwaitingClarification(Boolean(outcomeIntent && outcomeAwaitingClarification))
    setPrompt(prefillRequest)
    window.requestAnimationFrame?.(() => textareaRef.current?.focus())
  }, [outcomeAwaitingClarification, outcomeIntent, prefillRequest])

  useEffect(() => {
    if (!outcomeIntent) setAwaitingClarification(false);
  }, [outcomeIntent]);

  useEffect(() => {
    const activeRequestIdentity = activeRequestIdentityRef.current;
    if (
      activeRequestIdentity &&
      requestIdentity(outcomeDraftId, prefillRequest) !== activeRequestIdentity
    ) {
      abortRef.current?.abort();
    }
  }, [outcomeDraftId, prefillRequest]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  function handOffToChat(source: string) {
    setChatSidebarOpen(true)
    window.requestAnimationFrame?.(() => {
      window.dispatchEvent(
        new CustomEvent('cogo:focus-chat-input', {
          detail: { source },
        }),
      )
    })
  }

  // A1 typed-failure mapping — shared by the producer's onGenerationError
  // callback and the generic catch block below, so a raw internal message
  // (e.g. "Ontology admission blocked...") is NEVER shown to the creator
  // through either path. Never returns `rawMessage` itself.
  //
  // 2026-07-27 — the mapping used to collapse EVERY non-ontology failure into
  // `generic`, discarding the per-reason copy live-planner-copy.ts already
  // authors in five locales. It now routes the recognized reason to that
  // copy. Still a closed switch: `mapCreatorPlanningFailureMessage` returns
  // authored copy for a known reason and `t.planningFailure.generic` for
  // anything else, and the ontology-blocked branch is unchanged.
  function mapPlanningFailureMessage(
    rawMessage: string,
    ontologyIssueCodes?: readonly string[],
  ): string {
    return mapCreatorPlanningFailureMessage(
      rawMessage,
      locale,
      t.planningFailure,
      null,
      ontologyIssueCodes,
    );
  }

  // `overrideText` exists for the S2 confirm, which submits the AUGMENTED
  // prompt (table + decision block) in the same tick it calls setPrompt — the
  // `prompt` state read below would still be the pre-augmentation text. The
  // typeof guard matters: this function is also wired directly as an onClick
  // handler, where React passes the click event as the first argument.
  async function handleSubmit(overrideText?: unknown) {
    const text = (
      typeof overrideText === 'string' ? overrideText : prompt
    ).trim();
    if (!text || isCreating || submitLock.current) return;
    const selectedOutcomeIntent = outcomeIntent;
    const selectedOutcomeDraftId = outcomeDraftId;
    const generationLeaseId = crypto.randomUUID();
    if (!claimStudioGenerationRequest(generationLeaseId)) {
      setPlanningMessage(outcomeCopy.inProgress);
      return;
    }
    const requestSequence = ++requestSequenceRef.current
    const selectedRequestIdentity = requestIdentity(selectedOutcomeDraftId, prefillRequest)
    const isCurrentRequest = () => latestRequestIdentityRef.current === selectedRequestIdentity
    submitLock.current = true
    setIsCreating(true)
    setPlanningMessage(t.checkingSession)
    setPlanningError(null)
    setPlanningEvidence(null)
    setClarificationQuestions([])
    setInspectorPanelOpen(false)
    setPendingChatNotice(t.startingNotice)
    setConversionWithoutOriginalNotice(false)
    const controller = new AbortController()
    abortRef.current = controller
    activeRequestIdentityRef.current = selectedRequestIdentity
    let generationErrorSurfaced = false
    try {
      const designSystemPreset = activeDesignSystemPreset;
      // Studio Task 9 — the ONLY ontology-binding this surface may supply is
      // the explicit selection the server/project (or the Library item the
      // user picked) already persisted. Read verbatim through the Task 2
      // closed guard: no prompt text, application name, display title,
      // candidate rank or evidence realm is consulted, nothing is minted, and
      // the opaque project/actor scope refs are NOT authenticated here —
      // platform `runKgPreflight()` alone owns that.
      const persistedSelection = readPersistedApplicationOntologyGenerationSelection(
        useStudioStore.getState().projectSettings,
      )
      if (persistedSelection.kind === 'selection-missing') {
        // Bound project, no choice made yet. Pause with ZERO preflight,
        // decompose, model or project effects — never a "first candidate".
        setPlanningMessage(null)
        setPlanningEvidence(null)
        setPendingChatNotice(null)
        setPlanningError(t.ontologySelectionRequired)
        return
      }
      // A confirm click on an intake card (S1 contract, S2 field table)
      // authorizes ONE exact text — for S2 the augmented prompt, table plus
      // [field-visibility-decision]. That text goes straight to generation:
      // re-routing it could re-show a card, or worse let a 설명 cell's word
      // hand a human-confirmed spec to the template matcher.
      const confirmedIntake =
        confirmedIntakeRef.current?.prompt === text ? confirmedIntakeRef.current : null
      const intakeConfirmed = confirmedIntake !== null
      if (intakeConfirmed) confirmedIntakeRef.current = null
      const creationDecision = intakeConfirmed
        ? ({ kind: 'zero-base-generation', prompt: text } as const)
        : routeStudioCreationRequest(text)
      if (creationDecision.kind === 'contract-readback') {
        // S1 — the right first answer to a pasted contract is a read-back,
        // not generation. This submit shows the card and stops (the finally
        // block releases the lease); the card's confirm arms the ref and
        // re-submits, and an edited prompt is read back again.
        setContractCard({ readback: creationDecision.readback, prompt: text })
        setPlanningMessage(null)
        setPendingChatNotice(null)
        return
      }
      if (creationDecision.kind === 'multi-intake-readback') {
        // W21 rung ④ — several documents in one paste (explicit `---`
        // separators). Every part's card opens at once, each reading back
        // ITS OWN segment; the card states are independent so they stack in
        // the composer, and ANY confirm serializes ALL open cards into one
        // composite prompt (see buildCompositeIntakeConfirm).
        for (const part of creationDecision.parts) {
          if (part.kind === 'contract-readback') {
            setContractCard({ readback: part.readback, prompt: part.segment })
          } else if (part.kind === 'field-spec-readback') {
            setFieldSpecCard({
              readback: part.readback,
              prompt: part.segment,
              visible: part.readback.fields.map(() => true),
            })
          } else if (part.kind === 'legacy-screen-readback') {
            setLegacyCard({
              readback: part.readback,
              prompt: part.segment,
              rulings: {},
            })
          } else if (part.kind === 'screen-design-readback') {
            setDesignCard({ readback: part.readback, prompt: part.segment })
          } else {
            setReferenceCard({
              readback: part.readback,
              prompt: part.segment,
              structureCarried: true,
              termCarried: part.readback.terms.map(() => false),
            })
          }
        }
        setPlanningMessage(null)
        setPendingChatNotice(null)
        return
      }
      if (creationDecision.kind === 'field-spec-readback') {
        // S2 — a pasted field table's one open question is which fields the
        // screen shows versus travel internally. Everything starts visible;
        // unchecking is the deliberate act. Confirm serializes the pass and
        // re-submits the augmented prompt through the gate above.
        setFieldSpecCard({
          readback: creationDecision.readback,
          prompt: text,
          visible: creationDecision.readback.fields.map(() => true),
        })
        setPlanningMessage(null)
        setPendingChatNotice(null)
        return
      }
      if (creationDecision.kind === 'legacy-screen-readback') {
        // S3 — a conversion, not a new build: reusing the new-build flow
        // here is the failure mode the scenarios doc warns about. The card
        // shows the comparison and the carry-vs-improve list; every detected
        // trait starts at 'keep' (the developer asked for the same screen)
        // and each 'improve' is an explicit ruling.
        setLegacyCard({
          readback: creationDecision.readback,
          prompt: text,
          rulings: {},
        })
        setPlanningMessage(null)
        setPendingChatNotice(null)
        return
      }
      if (creationDecision.kind === 'screen-design-readback') {
        // S4 — the design document gave the screen; it did not give the
        // data. Say so before generation, and record proceeding screen-first
        // as the human's explicit decision.
        setDesignCard({ readback: creationDecision.readback, prompt: text })
        setPlanningMessage(null)
        setPendingChatNotice(null)
        return
      }
      if (creationDecision.kind === 'reference-extraction-readback') {
        // S7 — extract the standard from the reference, never clone it.
        // Structure carries by default; terms start as this screen's own and
        // each promotion to standard vocabulary is the human's act.
        setReferenceCard({
          readback: creationDecision.readback,
          prompt: text,
          structureCarried: true,
          termCarried: creationDecision.readback.terms.map(() => false),
        })
        setPlanningMessage(null)
        setPendingChatNotice(null)
        return
      }
      if (creationDecision.kind === 'template-proposal') {
        // Shown HERE, not handed to the rail. Home owns its conversation
        // (`conversationOwner: 'route'`), so there is no rail on this screen
        // to hand anything to — and the old handoff cleared the prompt on its
        // way out, which would have left a person with neither the card nor
        // the sentence they typed.
        //
        // The prompt deliberately STAYS in the composer, exactly as it does
        // behind every other read-back card above: the card is a question,
        // not a commitment, and editing the text takes the question back
        // (the textarea's onChange clears this alongside the others).
        setTemplateProposal({
          templateId: creationDecision.templateId,
          displayName: creationDecision.displayName,
          prompt: creationDecision.prompt,
          matchedTerms: creationDecision.matchedTerms,
        })
        setPlanningMessage(null)
        setPendingChatNotice(null)
        return
      }
      const nativeGeneration = await tryNativeUiBlockStudioGeneration(text)
      if (nativeGeneration.kind === 'generated') {
        setPlanningMessage(null)
        setPlanningEvidence(null)
        setPlanningError(null)
        setPrompt('')
        handOffToChat('home-generation-complete')
        return
      }
      if (nativeGeneration.kind === 'failed') {
        setPlanningEvidence(null)
        setPlanningMessage(null)
        setPlanningError(nativeGeneration.message)
        return
      }
      // Task 5 — this is the same `text` `startAppGenerationFromPrompt` is
      // about to derive `settings.asIsSnapshot` from (deriveAsIsSnapshotFromPrompt
      // inside it, Task 1), so the notice and the actual generation outcome
      // can never disagree. Fired here, right before generation starts, not
      // after it completes — telling the creator only once they have waited
      // removes their chance to supply the original instead.
      if (shouldNoticeConversionWithoutOriginal(startMode, text)) {
        setConversionWithoutOriginalNotice(true)
      }
      // 2026-08-17 wave-4 lane-2 — this branch is reached only when
      // `creationDecision.kind === 'zero-base-generation'` (every other kind
      // returned above), so `creationDecision.prompt` (== `text`) is exactly
      // what the deterministic classifier just read. Mint the provenance
      // here, next to that classification, and thread it in below --
      // `startAppGenerationFromPromptWithLease` verifies it (fails closed on
      // a forged claim) before any planning work.
      const generationTrigger = mintClassifierDispatchTrigger(
        creationDecision.prompt,
      )
      const started = await startAppGenerationFromPrompt(text, {
        trigger: generationTrigger,
        designSystemPreset,
        // Studio Task 9 — the one public bootstrap value, threaded through the
        // ORDINARY executor. Absent for ordinary unbound authoring, which is
        // byte-for-byte the pre-Task-9 call.
        ...(persistedSelection.kind === 'selected'
          ? { applicationOntologyBootstrap: persistedSelection.bootstrap }
          : {}),
        ...(selectedOutcomeIntent
          ? { outcomeIntent: selectedOutcomeIntent }
          : {}),
        // Task 3 fix-round-1/2 — `text` here is `confirmIntakeCards`'s
        // AUGMENTED prompt (this card's raw paste + the human-confirmed
        // decision block, joined with every other open card's segment on a
        // multi-document paste), not the original. `confirmedIntake` is the
        // SAME ref `confirmIntakeCards` set alongside building `augmented`
        // (never a separate condition on a specific card -- round 1 gated
        // this on `legacyCard` alone, which reproduced the identical bug on
        // the S7 reference lane), so `originalScreenMarkup` is the actual
        // pasted segment `deriveAsIsSnapshotFromPrompt` needs as the As-Is
        // markup source instead of `text`. Absent (no card carrying a screen
        // was confirmed -- every other submit), `text` remains the sole
        // source, unchanged.
        ...(confirmedIntake?.originalScreenMarkup
          ? { originalPastedMarkup: confirmedIntake.originalScreenMarkup }
          : {}),
        generationLeaseId,
        signal: controller.signal,
        // G3 — U2 judgment moment: the producer calls this only when admitted
        // library candidates exist for the plain creator path. Surfacing them
        // reuses the inline clarification grammar below; the promise resolves
        // on the creator's card click. Abort is handled producer-side (the
        // selection await races the same signal as the rest of the flow).
        //
        // FINDING-5 latency honesty — this callback is passed
        // UNCONDITIONALLY, so every plain submit (not just ones that end up
        // showing cards) pays the library lookup between planning success
        // and project creation: up to the lookup fetch bound (6s, see
        // LOOKUP_TIMEOUT_MS in library-structure-lookup.ts) when the proxy
        // is slow or down, before failing open to zero candidates. That is
        // the accepted price of the judgment moment on this surface.
        onLibraryStructureSelection: (candidates, judgment) =>
          new Promise<LibraryStructureSelectionChoice>((resolve) => {
            if (controller.signal.aborted || !isCurrentRequest()) {
              resolve({ kind: 'start-fresh' })
              return
            }
            // FINDING-1 — the producer bounds the judgment wait and fires
            // dismissSignal whenever the moment ends without a card click
            // (bounded-wait expiry, cancel). Reuse the selection path's
            // state-clearing so the hero never keeps showing stale cards for
            // a flow that has already moved on — clear only THIS judgment
            // (identified by its resolve fn), never a newer request's cards.
            judgment.dismissSignal.addEventListener(
              'abort',
              () => {
                setLibraryStructureJudgment((current) =>
                  current && current.resolve === resolve ? null : current,
                )
              },
              { once: true },
            )
            setPlanningMessage(null)
            setLibraryStructureJudgment({ candidates, resolve })
          }),
        onPlanningProgress: (progress) => {
          if (!isCurrentRequest()) return;
          setPlanningMessage(progress.message);
          const evidence = planningEvidenceFromProgress(progress);
          if (evidence) setPlanningEvidence(evidence);
        },
        onGenerationError: (message, details) => {
          if (!isCurrentRequest()) return;
          generationErrorSurfaced = true;
          // A1 — route through the same typed-failure mapping as the catch
          // block below instead of `toast.error(message)`: `message` here
          // is the producer's raw internal string (e.g. "Ontology admission
          // blocked..."), and Task 1's mounted <Toaster/> would render that
          // raw text verbatim beside the friendly inline alert otherwise.
          setPlanningMessage(null);
          setPlanningEvidence(null);
          setPlanningError(
            mapPlanningFailureMessage(message, details?.ontologyIssueCodes),
          );
        },
      });
      if (controller.signal.aborted || !isCurrentRequest()) {
        controller.abort();
        if (started.status === "started") {
          try {
            await started.generation;
          } catch {
            // Cancellation or rollback failure is surfaced by the producer.
          }
        }
        // Review I-2 — a cancelled run's evidence is stale the moment it is
        // cancelled, and unlike the success path there is no navigation to
        // unmount it: `start-app-generation-from-prompt.ts:773` pushes
        // `/ide/:id` only when a project is actually born. Left set, it is a
        // `composerSurfaces` member that nothing ever clears, so the composer
        // could never reach rest again.
        setPlanningEvidence(null);
        setPlanningMessage(t.cancelled);
        return;
      }
      if (started.status === 'clarification-required') {
        setAwaitingClarification(true)
        const questions = started.sourceSufficiency.questions
          .map((question) => question.question.trim())
          .filter(Boolean)
          .slice(0, 3)
        setClarificationQuestions(questions)
        if (selectedOutcomeIntent && selectedOutcomeDraftId) {
          onOutcomeDraftChange?.(selectedOutcomeDraftId, text, true);
        }
        setPlanningMessage(questions.length === 0 ? 'Source assessment is required before generation' : null)
        return
      }
      if (started.status === 'generation-readiness-blocked') {
        // Task 4 — the producer already wrote a chat notice; this inline
        // planning-error surface is this component's OWN durable copy of
        // the same refusal (mirrors onGenerationError's setPlanningError
        // below), using the readiness item's own already-user-facing
        // explanation directly rather than routing it through
        // mapPlanningFailureMessage (that mapper is for the producer's raw
        // internal strings, not for copy that is already written for
        // creators — the same reason the clarification branch above never
        // maps `question.question` either).
        const [firstBlocking] = started.readiness.items.filter(
          (item) => item.kind === 'blocking',
        )
        setPlanningEvidence(null)
        setPlanningError(
          firstBlocking?.explanation ?? 'This project is not ready to generate yet',
        )
        setPlanningMessage(null)
        return
      }
      setAwaitingClarification(false)
      setClarificationQuestions([])
      if (selectedOutcomeIntent && selectedOutcomeDraftId) {
        onOutcomeDraftChange?.(selectedOutcomeDraftId, text, false);
      }
      setPlanningEvidence(
        planningEvidenceFromMetadata(started.appSpec.decomposePlanning),
      );
      await started.generation;
      if (controller.signal.aborted || !isCurrentRequest()) {
        // Review I-2 — same as the pre-generation abort above.
        setPlanningEvidence(null);
        setPlanningMessage(t.cancelled);
        return;
      }
      if (selectedOutcomeIntent && selectedOutcomeDraftId) {
        onOutcomeIntentConsumed?.(selectedOutcomeDraftId);
      }
      setPrompt("");
      handOffToChat('home-generation-complete')
    } catch (err) {
      if (controller.signal.aborted || !isCurrentRequest()) {
        // Review I-2 — the cancel path a rejecting producer takes.
        setPlanningEvidence(null);
        setPlanningMessage(t.cancelled);
        return;
      }
      if (err instanceof StudioGenerationBusyError) {
        setPlanningMessage(outcomeCopy.inProgress);
        return;
      }
      // Studio Task 9 — a canonical refusal relayed by Task 2 is rendered as
      // `REFUSED/<code>`, verbatim and un-recoded. Nothing here writes an
      // evidence `BLOCKED`: the operational codes (PREFLIGHT_UNAVAILABLE,
      // ISSUANCE_UNAVAILABLE, ADMISSION_UNAVAILABLE) arrive on this very path
      // and are relayed with the same shape, never as a verdict.
      if (err instanceof StudioApplicationOntologyGenerationRefusal) {
        setPlanningEvidence(null);
        setPlanningMessage(null);
        setPlanningError(`REFUSED/${err.code}`);
        return;
      }
      console.error("[hero-chat] Create failed:", err);
      // Promoted carried note — always clear stale positive evidence on any
      // failure so a "Plan prepared" evidence panel never co-renders with a
      // failure alert, regardless of which branch below set the alert.
      setPlanningEvidence(null);
      // A1 — the generic catch path ALSO sets an inline, creator-facing
      // alert (never the raw `error.message`) that replaces the progress
      // box, so a fail-closed rejection is always visible even when a toast
      // has nowhere to render. Typed-failure mapping: an ontology-admission
      // rejection gets specific creator-language copy; everything else gets
      // a generic fallback. Guarded by `generationErrorSurfaced`: when the
      // producer already called `onGenerationError` for this exact failure
      // (it throws the same Error right after invoking the callback — see
      // start-app-generation-from-prompt.ts), the inline alert is already
      // set with the same mapped copy, so skip re-setting it here to avoid
      // a double-set/flash.
      if (!generationErrorSurfaced) {
        const rawMessage = err instanceof Error ? err.message : String(err);
        // The producer attaches the same codes to the thrown Error, so the
        // catch path names the same cause as the callback path.
        const carried = (err as unknown as { ontologyIssueCodes?: unknown })
          ?.ontologyIssueCodes;
        const codes = Array.isArray(carried)
          ? (carried as readonly string[])
          : undefined;
        setPlanningMessage(null);
        setPlanningError(mapPlanningFailureMessage(rawMessage, codes));
      }
      // F32/persona-ux-04 — shared handler: skip the generic toast when the
      // producer already surfaced a specific one, sign out on auth expiry,
      // else show the generic fallback. Same logic now runs at every caller.
      if (!generationErrorSurfaced) {
        handleProjectCreateError(err, {
          toastError: (m) => toast.error(m),
          signOut: () => void useStudioStore.getState().signOut(),
        });
      }
    } finally {
      releaseStudioGenerationRequest(generationLeaseId);
      // G3 — a judgment card left behind by an abort/failure must not outlive
      // its request (the producer-side await is already settled or rejected).
      setLibraryStructureJudgment(null);
      abortRef.current = null;
      if (activeRequestIdentityRef.current === selectedRequestIdentity) {
        activeRequestIdentityRef.current = null;
      }
      setIsCreating(false);
      submitLock.current = false;
      window.setTimeout(() => {
        if (requestSequenceRef.current === requestSequence) {
          setPlanningMessage(null);
        }
      }, 1500);
    }
  }

  function handleCancel() {
    abortRef.current?.abort();
  }

  /**
   * Serialize EVERY open intake card into one composite prompt and submit it
   * through the confirmed-intake gate (W21 rung ④). With a single open card
   * this is byte-for-byte the old per-card confirm (join of one part); with a
   * multi-document paste it is the whole point — each confirm button means
   * "이 결정들로 진행", never "이 카드만 진행하고 나머지는 버림". Segments are
   * re-joined with the same `---` separator the router split on, so the
   * decompose lane sees the documents exactly as the developer pasted them,
   * each followed by its human-confirmed decision block.
   */
  function confirmIntakeCards() {
    const parts: string[] = []
    // fix-round-2 (Task 3) — the As-Is markup source must be the RAW,
    // pre-augmentation segment of whichever card actually carries a pasted
    // ORIGINAL SCREEN, tracked HERE, next to the push that builds `parts`,
    // not as a separate condition elsewhere that can drift out of sync with
    // it (that drift is exactly how round 1 gated on `legacyCard` alone and
    // missed S7). Only S3 and S7 read back an actual screen; S1 (contract),
    // S2 (field table), and S4 (design document) read back non-markup data,
    // so they never set this. Adding a future card that carries a screen
    // means adding its raw segment here too.
    let originalScreenMarkup: string | undefined
    if (contractCard) parts.push(contractCard.prompt)
    if (fieldSpecCard) {
      parts.push(
        `${fieldSpecCard.prompt}\n\n${formatFieldVisibilityDecision(
          fieldSpecCard.readback,
          fieldSpecCard.visible,
        )}`,
      )
    }
    if (legacyCard) {
      originalScreenMarkup = legacyCard.prompt
      parts.push(
        `${legacyCard.prompt}\n\n${formatLegacyConversionDecision(
          legacyCard.readback,
          legacyCard.rulings,
        )}`,
      )
    }
    if (designCard) {
      parts.push(
        `${designCard.prompt}\n\n${formatScreenDesignDecision(designCard.readback)}`,
      )
    }
    if (referenceCard) {
      // BOTH-PRESENT RULE (fix-round-3) — S3 wins over S7. A legacy paste is
      // the screen being CONVERTED, so it is the 원본 the As-Is frame is
      // labelled for; a reference is a standard to extract from, and S7's
      // whole contract is that it is never cloned. Assigning unconditionally
      // here put the reference behind the 원본 label and DISCARDED the
      // converted screen, which is the one thing both scenarios agree must
      // not happen. `??=`, not `=`: the reference is the original only when
      // there is no converted screen to be the original instead.
      originalScreenMarkup ??= referenceCard.prompt
      parts.push(
        `${referenceCard.prompt}\n\n${formatReferenceExtractionDecision(
          referenceCard.readback,
          {
            structureCarried: referenceCard.structureCarried,
            termCarried: referenceCard.termCarried,
          },
        )}`,
      )
    }
    if (parts.length === 0) return
    const augmented = parts.join('\n\n---\n\n')
    confirmedIntakeRef.current = { prompt: augmented, originalScreenMarkup }
    setContractCard(null)
    setFieldSpecCard(null)
    setLegacyCard(null)
    setDesignCard(null)
    setReferenceCard(null)
    setPrompt(augmented)
    void handleSubmit(augmented)
  }

  // G3 — settle the pending U2 judgment exactly once and let the awaiting
  // generation flow continue with the creator's choice.
  function resolveLibraryStructureJudgment(
    choice: LibraryStructureSelectionChoice,
  ) {
    const judgment = libraryStructureJudgment;
    if (!judgment) return;
    setLibraryStructureJudgment(null);
    judgment.resolve(choice);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  /**
   * Carry the dropped file to the import page, or say why we cannot.
   *
   * The previous body was `if (files.length > 0) onSourceSelect?.()` — it read
   * the file count only to decide whether to navigate and then dropped the
   * files on the floor. Measured 2026-07-30: dropping a `.docx` here moved the
   * creator to `/studio/import` with no file name, no error and no request.
   * A source that cannot be forwarded must now produce a visible reason
   * instead of a silent navigation.
   */
  function stageSourceFiles(files: File[]) {
    if (files.length === 0) {
      // A drag with no files at all (text, a link, a browser tab). Nothing to
      // carry, so nothing is claimed — and no navigation either.
      setDropRejection({ fileName: null, extension: null })
      return
    }
    const [file] = files
    const verdict = classifyDroppedSourceFile(file.name)
    if (!verdict.ok) {
      setDropRejection({ fileName: file.name, extension: verdict.extension })
      return
    }
    if (verdict.kind === 'figma' && !figmaImportAvailable) {
      setDropRejection({ fileName: file.name, extension: verdict.kind })
      return
    }
    setDropRejection(null)
    stagePendingSourceDrop({
      kind: verdict.kind,
      file,
      droppedCount: files.length,
    })
    onSourceSelect?.()
  }

  function handleSourceDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(false)
    stageSourceFiles(Array.from(e.dataTransfer.files))
  }

  function handleSourcePick(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.currentTarget
    const files = Array.from(input.files ?? [])
    if (files.length > 0) stageSourceFiles(files)
    // Picking the same file twice must still emit `change` after a rejection
    // or a return from Import. The staged File object is already retained by
    // `stagePendingSourceDrop`, so clearing only resets picker chrome.
    input.value = ''
  }

  return (
    <div className={`flex flex-col items-center gap-4 px-4 ${showGreeting ? 'py-12' : 'py-0'} ${className}`.trim()}>
      {/* Greeting */}
      {showGreeting ? (
        <h1 className="text-3xl font-semibold tracking-tight text-foreground text-center">
          {t.greeting(displayName)}
        </h1>
      ) : null}

      {landingHandoffPrompt ? (
        <div
          data-testid="hero-landing-handoff"
          className="w-full max-w-3xl rounded-xl border border-[hsl(var(--brand-structure)/.22)] bg-[hsl(var(--brand-structure)/.05)] px-4 py-3 text-left"
        >
          <div className="text-xs font-semibold text-[hsl(var(--brand-structure))]">{renewalCopy.carriedRequest}</div>
          <p className="mt-1 text-sm leading-6 text-foreground">{renewalCopy.carriedRequestDetail}</p>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">“{landingHandoffPrompt}”</p>
        </div>
      ) : null}

      {/* A drop this composer could not forward. It replaces the old silent
          navigation, so an unsupported file is never mistaken for an accepted
          one. Dismissible, because it is about one gesture, not a state. */}
      {dropRejection ? (
        <div
          data-testid="hero-source-drop-rejected"
          role="status"
          className="flex w-full max-w-3xl items-start justify-between gap-3 rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-left"
        >
          <div className="min-w-0">
            <div className="text-xs font-semibold text-amber-700 dark:text-amber-300">
              {dropCopy.rejectedTitle}
            </div>
            <p className="mt-1 break-words text-sm leading-6 text-foreground">
              {dropRejection.fileName
                ? dropCopy.rejectedFile(dropRejection.fileName)
                : dropCopy.rejectedNoFile}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {dropCopy.accepted(availableSourceExtensions.join(', '))}
            </p>
          </div>
          <button
            type="button"
            aria-label={t.cancel}
            onClick={() => setDropRejection(null)}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : null}

      {/* Task 5 — "conversion" was chosen but the submitted text carried no
          original a screen could be derived from, so this run proceeds as
          new. Fired at submit time (see `shouldNoticeConversionWithoutOriginal`
          in handleSubmit), before generation runs — not after, when it would
          be too late for the creator to supply the original. Same
          dismissible-banner idiom as `dropRejection` above: about one
          submission, not an ongoing state. */}
      {conversionWithoutOriginalNotice ? (
        <div
          data-testid="hero-conversion-without-original"
          role="status"
          className="flex w-full max-w-3xl items-start justify-between gap-3 rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-left"
        >
          <p className="min-w-0 text-sm leading-6 text-foreground">
            {renewalCopy.conversionWithoutOriginal}
          </p>
          <button
            type="button"
            aria-label={t.cancel}
            onClick={() => setConversionWithoutOriginalNotice(false)}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : null}

      {/* Start-mode chip — conversation state only, not persisted. The truth
          of "this is a conversion" is `settings.asIsSnapshot` on the
          project, never this choice; it exists only to ask for the
          original up front. */}
      <div
        className="flex flex-row gap-2 pb-2"
        role="group"
        aria-label={renewalCopy.startModeGroupLabel}
      >
        {(['new', 'conversion'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            data-testid={`start-mode-${mode}`}
            aria-pressed={startMode === mode}
            onClick={() => setStartMode(mode)}
            className={
              startMode === mode
                ? 'rounded-full bg-primary px-3 py-1 text-sm text-primary-foreground'
                : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground'
            }
          >
            {mode === 'new' ? renewalCopy.startModeNew : renewalCopy.startModeConversion}
          </button>
        ))}
      </div>
      {startMode === 'conversion' ? (
        <p data-testid="conversion-ask" className="pb-2 text-sm text-muted-foreground">
          {renewalCopy.conversionAsk}
        </p>
      ) : null}

      {/* One conversation input for new work, existing products and sources. */}
      <div className="w-full max-w-3xl">
        <div
          data-testid="hero-chat-input-shell"
          data-drag-active={isDragActive ? 'true' : 'false'}
          onDragEnter={(e) => {
            e.preventDefault()
            setIsDragActive(true)
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setIsDragActive(false)
            }
          }}
          onDrop={handleSourceDrop}
          className={`overflow-hidden rounded-2xl border bg-card shadow-[0_4px_20px_rgb(15_23_42_/_0.06)] transition-[border-color,box-shadow,background-color] focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20 ${
            isDragActive ? 'border-[hsl(var(--brand-source))] bg-[hsl(var(--brand-source)/.05)]' : 'border-[#E9EDF2]'
          }`}
        >
          {outcomeIntent && selectedOutcomeCopy ? (
            <div className="flex items-center justify-between gap-2 px-3 pt-3">
              <span
                data-testid="hero-outcome-intent"
                data-sku-id={outcomeIntent.skuId}
                className="inline-flex min-w-0 items-center gap-2 rounded-md border border-border bg-muted/50 px-2.5 py-1 text-xs text-foreground"
              >
                <span className="truncate font-medium">
                  {selectedOutcomeCopy.title}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {outcomeCopy.eyebrow}
                </span>
              </span>
              <button
                type="button"
                aria-label={outcomeCopy.clearAction}
                onClick={() => {
                  setAwaitingClarification(false);
                  if (outcomeDraftId) {
                    onOutcomeIntentConsumed?.(outcomeDraftId);
                  }
                }}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          ) : null}

          {!ariaDescribedBy ? (
            <p id={composerDescriptionId} className="sr-only">
              {renewalCopy.conversationHint}
            </p>
          ) : null}

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            data-testid="hero-chat-input"
            data-hero-chat
            data-focus-ring-owner="parent"
            aria-label={strings.home.startWithChat}
            aria-describedby={composerDescriptionId}
            value={prompt}
            onChange={(e) => {
              if (outcomeIntent && !awaitingClarification) {
                if (outcomeDraftId) {
                  onOutcomeIntentConsumed?.(outcomeDraftId);
                }
              } else if (outcomeIntent && outcomeDraftId) {
                onOutcomeDraftChange?.(
                  outcomeDraftId,
                  e.target.value,
                  true,
                );
              }
              setPrompt(e.target.value);
              // An edited prompt invalidates a shown read-back: the card
              // described the previous text, and the next submit re-reads.
              setContractCard(null);
              setFieldSpecCard(null);
              setLegacyCard(null);
              setDesignCard(null);
              setReferenceCard(null);
              setTemplateProposal(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder={homeState === 'first' ? renewalCopy.firstPlaceholder : renewalCopy.returningPlaceholder}
            disabled={isCreating}
            rows={1}
            className="min-h-[74px] w-full resize-none border-0 bg-transparent px-5 pb-2 pt-[18px] text-[14.5px] leading-6 text-foreground placeholder:text-[#64748B] focus:outline-none disabled:opacity-50 sm:min-h-[50px]"
          />

          {contractCard ? (
            <div
              data-testid="hero-contract-readback"
              className="mx-4 mb-3 rounded-xl border border-[hsl(var(--brand-structure)/.25)] bg-[hsl(var(--brand-structure)/.05)] px-3.5 py-3"
            >
              <div className="text-xs font-semibold text-[hsl(var(--brand-structure))]">
                {renewalCopy.contractReadback.title}
                {contractCard.readback.contractId
                  ? ` · ${contractCard.readback.contractId}`
                  : ''}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {renewalCopy.contractReadback.endpointCount.replace(
                  '{count}',
                  String(contractCard.readback.endpoints.length),
                )}
                {' · '}
                {renewalCopy.contractReadback.fieldCount.replace(
                  '{count}',
                  String(contractCard.readback.fieldCount),
                )}
              </p>
              <ul className="mt-2 space-y-1 text-sm leading-5 text-foreground">
                {contractCard.readback.endpoints.map((endpoint) => (
                  <li
                    key={`${endpoint.method} ${endpoint.path}`}
                    className="flex items-baseline gap-2"
                  >
                    <span className="shrink-0 font-mono text-xs text-[hsl(var(--brand-structure))]">
                      {endpoint.method}
                    </span>
                    <span className="break-all font-mono text-xs">{endpoint.path}</span>
                    {endpoint.summary ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {endpoint.summary}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              {contractCard.readback.unfilledResponseFields.length > 0 ? (
                <p
                  data-testid="hero-contract-readback-unfilled"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.contractReadback.unfilledWarning.replace(
                    '{count}',
                    String(contractCard.readback.unfilledResponseFields.length),
                  )}{' '}
                  <span className="font-mono">
                    {contractCard.readback.unfilledResponseFields
                      .map((field) => field.fieldName)
                      .join(', ')}
                  </span>
                </p>
              ) : null}
              {contractCard.readback.unread.types.length > 0 ? (
                <p
                  data-testid="hero-contract-readback-unread"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.contractReadback.unreadTypes.replace(
                    '{count}',
                    String(contractCard.readback.unread.types.length),
                  )}{' '}
                  <span className="font-mono">
                    {contractCard.readback.unread.types.join(', ')}
                  </span>
                </p>
              ) : null}
              {contractCard.readback.ambiguousCount > 0 ? (
                <p
                  data-testid="hero-contract-readback-ambiguous"
                  className="mt-2 text-xs leading-5 text-muted-foreground"
                >
                  {renewalCopy.contractReadback.ambiguousNote.replace(
                    '{count}',
                    String(contractCard.readback.ambiguousCount),
                  )}
                </p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="hero-contract-readback-confirm"
                  onClick={() => {
                    if (!contractCard) return
                    confirmIntakeCards()
                  }}
                  className="rounded-lg bg-[hsl(var(--brand-structure))] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-structure))]/40"
                >
                  {renewalCopy.contractReadback.confirm}
                </button>
                <button
                  type="button"
                  data-testid="hero-contract-readback-dismiss"
                  onClick={() => setContractCard(null)}
                  className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {renewalCopy.contractReadback.dismiss}
                </button>
              </div>
            </div>
          ) : null}

          {fieldSpecCard ? (
            <div
              data-testid="hero-field-spec-readback"
              className="mx-4 mb-3 rounded-xl border border-[hsl(var(--brand-structure)/.25)] bg-[hsl(var(--brand-structure)/.05)] px-3.5 py-3"
            >
              <div className="text-xs font-semibold text-[hsl(var(--brand-structure))]">
                {renewalCopy.fieldSpecReadback.title}
                {' · '}
                {renewalCopy.fieldSpecReadback.fieldCount.replace(
                  '{count}',
                  String(fieldSpecCard.readback.fields.length),
                )}
              </div>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {renewalCopy.fieldSpecReadback.hint}
              </p>
              {(['input', 'output', 'common', 'unknown'] as const)
                .filter(
                  (section) => fieldSpecCard.readback.sectionCounts[section] > 0,
                )
                .map((section) => {
                  const sectionIndexes = fieldSpecCard.readback.fields
                    .map((field, index) => ({ field, index }))
                    .filter(({ field }) => field.section === section)
                  const allVisible = sectionIndexes.every(
                    ({ index }) => fieldSpecCard.visible[index],
                  )
                  return (
                    <div key={section} className="mt-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-foreground">
                          {renewalCopy.fieldSpecReadback.sections[section]}
                          {' · '}
                          {sectionIndexes.length}
                        </span>
                        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                          <input
                            type="checkbox"
                            data-testid={`hero-field-spec-section-all-${section}`}
                            checked={allVisible}
                            onChange={(event) => {
                              const next = event.target.checked
                              setFieldSpecCard((current) => {
                                if (!current) return current
                                const visible = [...current.visible]
                                for (const { index } of sectionIndexes)
                                  visible[index] = next
                                return { ...current, visible }
                              })
                            }}
                            className="rounded border-border"
                          />
                          {renewalCopy.fieldSpecReadback.sectionAllLabel}
                        </label>
                      </div>
                      <div className="mt-1.5 space-y-1">
                        {sectionIndexes.map(({ field, index }) => (
                          <label
                            key={`${section}:${field.name}:${index}`}
                            className="flex cursor-pointer items-baseline gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-muted/40"
                          >
                            <input
                              type="checkbox"
                              data-testid={`hero-field-spec-field-${field.name}`}
                              checked={fieldSpecCard.visible[index]}
                              onChange={(event) => {
                                const next = event.target.checked
                                setFieldSpecCard((current) => {
                                  if (!current) return current
                                  const visible = [...current.visible]
                                  visible[index] = next
                                  return { ...current, visible }
                                })
                              }}
                              className="translate-y-0.5 rounded border-border"
                            />
                            <span className="shrink-0 font-mono text-xs text-foreground">
                              {field.name}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {[
                                field.type,
                                field.length,
                                // Tri-state, on purpose: `required === null`
                                // means the table did not say (or its column
                                // was not read) and renders NOTHING, which
                                // must not look like the table saying
                                // "optional".
                                field.required === true
                                  ? renewalCopy.fieldSpecReadback.requiredMark
                                  : field.required === false
                                    ? renewalCopy.fieldSpecReadback.optionalMark
                                    : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                            {field.description ? (
                              <span className="truncate text-[11px] text-muted-foreground">
                                {field.description}
                              </span>
                            ) : null}
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })}
              {/*
                What the paste carried and this card does not show. Without
                these two lines an unread column is pixel-identical to a
                column that said nothing, and a dropped row is pixel-identical
                to a table that never had it.
              */}
              {fieldSpecCard.readback.unread.columns.length > 0 ? (
                <p
                  data-testid="hero-field-spec-unread-columns"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.fieldSpecReadback.unreadColumns.replace(
                    '{count}',
                    String(fieldSpecCard.readback.unread.columns.length),
                  )}{' '}
                  <span className="font-mono">
                    {fieldSpecCard.readback.unread.columns.join(', ')}
                  </span>
                </p>
              ) : null}
              {fieldSpecCard.readback.unread.skippedRows > 0 ? (
                <p
                  data-testid="hero-field-spec-unread-rows"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.fieldSpecReadback.unreadRows.replace(
                    '{count}',
                    String(fieldSpecCard.readback.unread.skippedRows),
                  )}
                </p>
              ) : null}
              {/*
                I4 — the required column WAS found; these are the values in it
                the reader could not interpret. Its own notice, not a line in
                `unread-columns`: the column is read, and saying otherwise
                would be the opposite lie. Without it, a 필수구분 column of
                예/아니오 renders exactly like a table with no required column
                — which is the state the header widening created by making
                the column recognized and its values still unreadable.
              */}
              {fieldSpecCard.readback.unread.requiredValues.length > 0 ? (
                <p
                  data-testid="hero-field-spec-unread-required"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.fieldSpecReadback.unreadRequiredValues}{' '}
                  <span className="font-mono">
                    {fieldSpecCard.readback.unread.requiredValues.join(', ')}
                  </span>
                </p>
              ) : null}
              <p
                data-testid="hero-field-spec-summary"
                className="mt-2 text-xs text-muted-foreground"
              >
                {renewalCopy.fieldSpecReadback.summary
                  .replace(
                    '{visible}',
                    String(
                      fieldSpecCard.visible.filter(Boolean).length,
                    ),
                  )
                  .replace(
                    '{internal}',
                    String(
                      fieldSpecCard.visible.filter((v) => !v).length,
                    ),
                  )}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="hero-field-spec-confirm"
                  onClick={() => {
                    if (!fieldSpecCard) return
                    // Composite confirm (W21 ④): serializes EVERY open
                    // intake card — with one card open this is the old
                    // behavior exactly.
                    confirmIntakeCards()
                  }}
                  className="rounded-lg bg-[hsl(var(--brand-structure))] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-structure))]/40"
                >
                  {renewalCopy.fieldSpecReadback.confirm}
                </button>
                <button
                  type="button"
                  data-testid="hero-field-spec-dismiss"
                  onClick={() => setFieldSpecCard(null)}
                  className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {renewalCopy.fieldSpecReadback.dismiss}
                </button>
              </div>
            </div>
          ) : null}

          {legacyCard ? (
            <div
              data-testid="hero-legacy-readback"
              className="mx-4 mb-3 rounded-xl border border-[hsl(var(--brand-structure)/.25)] bg-[hsl(var(--brand-structure)/.05)] px-3.5 py-3"
            >
              <div className="text-xs font-semibold text-[hsl(var(--brand-structure))]">
                {renewalCopy.legacyScreenReadback.title}
              </div>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {renewalCopy.legacyScreenReadback.hint}
              </p>

              {/* Comparison: what was read ↔ what conversion does with it. */}
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {renewalCopy.legacyScreenReadback.readColumn}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs leading-5 text-foreground">
                    {legacyCard.readback.fields.length > 0 ? (
                      <li>
                        {renewalCopy.legacyScreenReadback.fieldsLine.replace(
                          '{count}',
                          String(legacyCard.readback.fields.length),
                        )}
                        {' — '}
                        <span className="font-mono text-[11px]">
                          {legacyCard.readback.fields
                            .slice(0, 6)
                            .map((field) => field.name)
                            .join(', ')}
                          {legacyCard.readback.fields.length > 6
                            ? ` +${legacyCard.readback.fields.length - 6}`
                            : ''}
                        </span>
                      </li>
                    ) : null}
                    {legacyCard.readback.tables.length > 0 ? (
                      <li>
                        {renewalCopy.legacyScreenReadback.tablesLine.replace(
                          '{count}',
                          String(legacyCard.readback.tables.length),
                        )}
                        {(() => {
                          const withColumns = legacyCard.readback.tables.find(
                            (table) => table.columns.length > 0,
                          )
                          return withColumns ? (
                            <span className="font-mono text-[11px]">
                              {' — '}
                              {withColumns.columns.slice(0, 6).join(' · ')}
                            </span>
                          ) : null
                        })()}
                      </li>
                    ) : null}
                    {legacyCard.readback.actions.length > 0 ? (
                      <li>
                        {renewalCopy.legacyScreenReadback.actionsLine.replace(
                          '{count}',
                          String(legacyCard.readback.actions.length),
                        )}
                        {' — '}
                        {legacyCard.readback.actions
                          .slice(0, 6)
                          .map((action) => action.label)
                          .join(', ')}
                      </li>
                    ) : null}
                    {legacyCard.readback.scriptletCount > 0 ? (
                      <li>
                        {renewalCopy.legacyScreenReadback.scriptletsLine.replace(
                          '{count}',
                          String(legacyCard.readback.scriptletCount),
                        )}
                      </li>
                    ) : null}
                    {legacyCard.readback.inlineEventHandlerCount > 0 ? (
                      <li>
                        {renewalCopy.legacyScreenReadback.inlineHandlersLine.replace(
                          '{count}',
                          String(legacyCard.readback.inlineEventHandlerCount),
                        )}
                      </li>
                    ) : null}
                  </ul>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {renewalCopy.legacyScreenReadback.convertColumn}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs leading-5 text-foreground">
                    {legacyCard.readback.fields.length > 0 ? (
                      <li>
                        {renewalCopy.legacyScreenReadback.convertFieldsLine.replace(
                          '{count}',
                          String(legacyCard.readback.fields.length),
                        )}
                      </li>
                    ) : null}
                    {legacyCard.readback.tables.length > 0 ? (
                      <li>{renewalCopy.legacyScreenReadback.convertTablesLine}</li>
                    ) : null}
                    {legacyCard.readback.actions.length > 0 ? (
                      <li>
                        {renewalCopy.legacyScreenReadback.convertActionsLine.replace(
                          '{count}',
                          String(legacyCard.readback.actions.length),
                        )}
                      </li>
                    ) : null}
                    {legacyCard.readback.scriptletCount > 0 ? (
                      <li className="text-amber-700 dark:text-amber-400">
                        {renewalCopy.legacyScreenReadback.convertScriptletsLine}
                      </li>
                    ) : null}
                    {legacyCard.readback.inlineEventHandlerCount > 0 ? (
                      <li>
                        {
                          renewalCopy.legacyScreenReadback
                            .convertInlineHandlersLine
                        }
                      </li>
                    ) : null}
                  </ul>
                </div>
              </div>

              {legacyCard.readback.traits.length > 0 ? (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-foreground">
                    {renewalCopy.legacyScreenReadback.decisionsTitle}
                  </div>
                  <div className="mt-1.5 space-y-1">
                    {legacyCard.readback.traits.map((trait) => {
                      const ruling = legacyCard.rulings[trait.key] ?? 'keep'
                      const traitLabel = {
                        'table-layout':
                          renewalCopy.legacyScreenReadback.traits.tableLayout,
                        'alert-confirm':
                          renewalCopy.legacyScreenReadback.traits.alertConfirm,
                        'popup-window':
                          renewalCopy.legacyScreenReadback.traits.popupWindow,
                        'deprecated-markup':
                          renewalCopy.legacyScreenReadback.traits
                            .deprecatedMarkup,
                      }[trait.key]
                      const setRuling = (next: 'keep' | 'improve') =>
                        setLegacyCard((current) =>
                          current
                            ? {
                                ...current,
                                rulings: {
                                  ...current.rulings,
                                  [trait.key]: next,
                                },
                              }
                            : current,
                        )
                      return (
                        <div
                          key={trait.key}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md px-1.5 py-1"
                        >
                          <span className="text-xs text-foreground">
                            {traitLabel}
                            <span className="text-muted-foreground">
                              {' '}
                              · {trait.count}
                            </span>
                          </span>
                          <span className="flex overflow-hidden rounded-md border border-border">
                            <button
                              type="button"
                              data-testid={`hero-legacy-trait-${trait.key}-keep`}
                              aria-pressed={ruling === 'keep'}
                              onClick={() => setRuling('keep')}
                              className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${
                                ruling === 'keep'
                                  ? 'bg-[hsl(var(--brand-structure))] text-white'
                                  : 'bg-card text-muted-foreground hover:bg-muted/40'
                              }`}
                            >
                              {renewalCopy.legacyScreenReadback.keepLabel}
                            </button>
                            <button
                              type="button"
                              data-testid={`hero-legacy-trait-${trait.key}-improve`}
                              aria-pressed={ruling === 'improve'}
                              onClick={() => setRuling('improve')}
                              className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${
                                ruling === 'improve'
                                  ? 'bg-[hsl(var(--brand-structure))] text-white'
                                  : 'bg-card text-muted-foreground hover:bg-muted/40'
                              }`}
                            >
                              {renewalCopy.legacyScreenReadback.improveLabel}
                            </button>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  <p
                    data-testid="hero-legacy-summary"
                    className="mt-1.5 text-xs text-muted-foreground"
                  >
                    {renewalCopy.legacyScreenReadback.summary
                      .replace(
                        '{keep}',
                        String(
                          legacyCard.readback.traits.filter(
                            (trait) =>
                              (legacyCard.rulings[trait.key] ?? 'keep') ===
                              'keep',
                          ).length,
                        ),
                      )
                      .replace(
                        '{improve}',
                        String(
                          legacyCard.readback.traits.filter(
                            (trait) =>
                              legacyCard.rulings[trait.key] === 'improve',
                          ).length,
                        ),
                      )}
                  </p>
                </div>
              ) : null}

              {legacyCard.readback.scriptletCount > 0 ? (
                <p
                  data-testid="hero-legacy-data-warning"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.legacyScreenReadback.dataWarning.replace(
                    '{count}',
                    String(legacyCard.readback.scriptletCount),
                  )}
                </p>
              ) : null}

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="hero-legacy-confirm"
                  onClick={() => {
                    if (!legacyCard) return
                    confirmIntakeCards()
                  }}
                  className="rounded-lg bg-[hsl(var(--brand-structure))] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-structure))]/40"
                >
                  {renewalCopy.legacyScreenReadback.confirm}
                </button>
                <button
                  type="button"
                  data-testid="hero-legacy-dismiss"
                  onClick={() => setLegacyCard(null)}
                  className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {renewalCopy.legacyScreenReadback.dismiss}
                </button>
              </div>
            </div>
          ) : null}

          {designCard ? (
            <div
              data-testid="hero-design-readback"
              className="mx-4 mb-3 rounded-xl border border-[hsl(var(--brand-structure)/.25)] bg-[hsl(var(--brand-structure)/.05)] px-3.5 py-3"
            >
              <div className="text-xs font-semibold text-[hsl(var(--brand-structure))]">
                {renewalCopy.screenDesignReadback.title}
                {designCard.readback.title
                  ? ` · ${designCard.readback.title}`
                  : ''}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {renewalCopy.screenDesignReadback.countsLine
                  .replace('{areas}', String(designCard.readback.areas.length))
                  .replace(
                    '{labels}',
                    String(designCard.readback.labels.length),
                  )
                  .replace('{flows}', String(designCard.readback.flows.length))}
              </p>

              <div className="mt-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {renewalCopy.screenDesignReadback.areasTitle}
                </div>
                <ul
                  data-testid="hero-design-areas"
                  className="mt-1 space-y-0.5 text-xs leading-5 text-foreground"
                >
                  {designCard.readback.areas.map((area, index) => (
                    <li key={`${index}:${area}`}>{area}</li>
                  ))}
                </ul>
              </div>

              {designCard.readback.labels.length > 0 ? (
                <div className="mt-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {renewalCopy.screenDesignReadback.labelsTitle}
                  </div>
                  <p className="mt-1 flex flex-wrap gap-1">
                    {designCard.readback.labels.slice(0, 12).map((label) => (
                      <span
                        key={label}
                        className="rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[11px] text-foreground"
                      >
                        {label}
                      </span>
                    ))}
                    {designCard.readback.labels.length > 12 ? (
                      <span className="px-1 text-[11px] text-muted-foreground">
                        +{designCard.readback.labels.length - 12}
                      </span>
                    ) : null}
                  </p>
                </div>
              ) : null}

              {designCard.readback.flows.length > 0 ? (
                <div className="mt-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {renewalCopy.screenDesignReadback.flowsTitle}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs leading-5 text-foreground">
                    {designCard.readback.flows.map((flow, index) => (
                      <li key={`${index}:${flow}`}>{flow}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/*
                I1 — a heading this read RECOGNIZED and could place nowhere:
                not an area (it names a flow/process section), not the title,
                and its section holds no arrow line so it is not under the
                flow list either. Same visual weight as the data-gap notice
                below, and the heading's OWN WORDS rather than a count: the
                reader has to be able to find it in their document, and
                `countsLine` already stated the shrunken number.
              */}
              {designCard.readback.unread.headings.length > 0 ? (
                <p
                  data-testid="hero-design-unread-headings"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.screenDesignReadback.unreadHeadings}{' '}
                  <span className="font-mono">
                    {designCard.readback.unread.headings.join(', ')}
                  </span>
                </p>
              ) : null}

              {/*
                S4's core sentence — the screen is here, the data is not —
                is a CLAIM ABOUT THE DOCUMENT, so it renders only where the
                read found no data reference. Where it found some, the card
                shows what it found instead: the same card, telling the truth
                in both directions.
              */}
              {designCard.readback.dataDeclarations.length > 0 ? (
                <p
                  data-testid="hero-design-data-declared"
                  className="mt-2 rounded-md border border-[hsl(var(--brand-structure)/.35)] bg-[hsl(var(--brand-structure)/.08)] px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.screenDesignReadback.dataDeclared}{' '}
                  <span className="font-mono">
                    {designCard.readback.dataDeclarations.join(', ')}
                  </span>
                </p>
              ) : (
                <p
                  data-testid="hero-design-data-gap"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.screenDesignReadback.dataGap}
                </p>
              )}

              {/*
                N2 — code-shaped tokens the read saw and could not confirm.
                Rendered under EITHER data line, because it qualifies both:
                beside the gap notice it is what the human is actually being
                asked to rule on (confirming an absence they were never shown
                is not a confirmation), and beside the declared list it is why
                that list is what was READ, not the document's complete set.
                Absent whenever the read is confident — a screen id never
                appears here, so the channel does not fire on every document.
              */}
              {designCard.readback.unread.dataCandidates.length > 0 ? (
                <p
                  data-testid="hero-design-data-candidates"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.screenDesignReadback.dataCandidates}{' '}
                  <span className="font-mono">
                    {designCard.readback.unread.dataCandidates.join(', ')}
                  </span>
                </p>
              ) : null}

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="hero-design-proceed"
                  onClick={() => {
                    if (!designCard) return
                    confirmIntakeCards()
                  }}
                  className="rounded-lg bg-[hsl(var(--brand-structure))] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-structure))]/40"
                >
                  {designCard.readback.dataDeclarations.length > 0
                    ? renewalCopy.screenDesignReadback.proceedWithData
                    : renewalCopy.screenDesignReadback.proceed}
                </button>
                <button
                  type="button"
                  data-testid="hero-design-dismiss"
                  onClick={() => setDesignCard(null)}
                  className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {renewalCopy.screenDesignReadback.dismiss}
                </button>
              </div>
            </div>
          ) : null}

          {referenceCard ? (
            <div
              data-testid="hero-reference-readback"
              className="mx-4 mb-3 rounded-xl border border-[hsl(var(--brand-structure)/.25)] bg-[hsl(var(--brand-structure)/.05)] px-3.5 py-3"
            >
              <div className="text-xs font-semibold text-[hsl(var(--brand-structure))]">
                {renewalCopy.referenceExtractionReadback.title}
              </div>
              <p className="mt-1 text-xs text-foreground">
                <span className="text-muted-foreground">
                  {renewalCopy.referenceExtractionReadback.intentLabel}
                </span>{' '}
                {referenceCard.readback.intent}
              </p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {renewalCopy.referenceExtractionReadback.hint}
              </p>

              <label className="mt-2 flex cursor-pointer items-baseline gap-2">
                <input
                  type="checkbox"
                  data-testid="hero-reference-structure"
                  checked={referenceCard.structureCarried}
                  onChange={(event) => {
                    const next = event.target.checked
                    setReferenceCard((current) =>
                      current ? { ...current, structureCarried: next } : current,
                    )
                  }}
                  className="translate-y-0.5 rounded border-border"
                />
                <span className="text-xs font-medium text-foreground">
                  {renewalCopy.referenceExtractionReadback.structureLabel}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {referenceCard.readback.reference.kind === 'legacy-screen'
                    ? renewalCopy.referenceExtractionReadback.structureLegacyLine
                        .replace(
                          '{fields}',
                          String(
                            referenceCard.readback.reference.readback.fields
                              .length,
                          ),
                        )
                        .replace(
                          '{tables}',
                          String(
                            referenceCard.readback.reference.readback.tables
                              .length,
                          ),
                        )
                        .replace(
                          '{actions}',
                          String(
                            referenceCard.readback.reference.readback.actions
                              .length,
                          ),
                        )
                    : renewalCopy.referenceExtractionReadback.structureDesignLine
                        .replace(
                          '{areas}',
                          String(
                            referenceCard.readback.reference.readback.areas
                              .length,
                          ),
                        )
                        .replace(
                          '{flows}',
                          String(
                            referenceCard.readback.reference.readback.flows
                              .length,
                          ),
                        )}
                </span>
              </label>

              {referenceCard.readback.terms.length > 0 ? (
                <div className="mt-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {renewalCopy.referenceExtractionReadback.termsTitle}
                    </span>
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        data-testid="hero-reference-all-terms"
                        checked={referenceCard.termCarried.every(Boolean)}
                        onChange={(event) => {
                          const next = event.target.checked
                          setReferenceCard((current) =>
                            current
                              ? {
                                  ...current,
                                  termCarried: current.termCarried.map(
                                    () => next,
                                  ),
                                }
                              : current,
                          )
                        }}
                        className="rounded border-border"
                      />
                      {renewalCopy.referenceExtractionReadback.allLabel}
                    </label>
                  </div>
                  <p className="mt-1 flex flex-wrap gap-1">
                    {(() => {
                      const verdicts = checkTermsAgainstDictionary(
                        referenceCard.readback.terms,
                      )
                      return referenceCard.readback.terms.map((term, index) => {
                      const carried = referenceCard.termCarried[index]
                      const verdict = verdicts[index]
                      return (
                        <button
                          key={term}
                          type="button"
                          data-testid={`hero-reference-term-${term}`}
                          data-dictionary={verdict.inDictionary ? (verdict.exact ? 'exact' : 'candidate') : 'undefined'}
                          title={
                            verdict.inDictionary
                              ? renewalCopy.referenceExtractionReadback.dictionaryHit
                              : renewalCopy.referenceExtractionReadback.dictionaryMiss
                          }
                          aria-pressed={carried}
                          onClick={() =>
                            setReferenceCard((current) => {
                              if (!current) return current
                              const termCarried = [...current.termCarried]
                              termCarried[index] = !termCarried[index]
                              return { ...current, termCarried }
                            })
                          }
                          className={`rounded border px-1.5 py-0.5 font-mono text-[11px] transition-colors ${
                            carried
                              ? 'border-[hsl(var(--brand-structure))] bg-[hsl(var(--brand-structure))] text-white'
                              : 'border-border bg-card text-foreground hover:bg-muted/40'
                          }`}
                        >
                          {term}
                          {verdict.inDictionary ? (
                            <span aria-hidden className="ml-1 text-[9px] opacity-80">
                              ▪사전
                            </span>
                          ) : null}
                        </button>
                      )
                      })
                    })()}
                  </p>
                  <p
                    data-testid="hero-reference-summary"
                    className="mt-1.5 text-xs text-muted-foreground"
                  >
                    {renewalCopy.referenceExtractionReadback.summary
                      .replace(
                        '{standard}',
                        String(
                          referenceCard.termCarried.filter(Boolean).length,
                        ),
                      )
                      .replace(
                        '{specific}',
                        String(
                          referenceCard.termCarried.filter((v) => !v).length,
                        ),
                      )}
                  </p>
                </div>
              ) : null}

              {referenceCard.readback.identifierCount > 0 ? (
                <p
                  data-testid="hero-reference-identifiers"
                  className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs leading-5 text-foreground"
                >
                  {renewalCopy.referenceExtractionReadback.identifiersNote.replace(
                    '{count}',
                    String(referenceCard.readback.identifierCount),
                  )}
                </p>
              ) : null}

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="hero-reference-confirm"
                  onClick={() => {
                    if (!referenceCard) return
                    confirmIntakeCards()
                  }}
                  className="rounded-lg bg-[hsl(var(--brand-structure))] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-structure))]/40"
                >
                  {renewalCopy.referenceExtractionReadback.confirm}
                </button>
                <button
                  type="button"
                  data-testid="hero-reference-dismiss"
                  onClick={() => setReferenceCard(null)}
                  className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {renewalCopy.referenceExtractionReadback.dismiss}
                </button>
              </div>
            </div>
          ) : null}

          {/* The template proposal, on Home's own surface.
              Rendered UNCONDITIONALLY whenever there is one — never gated on
              the chat rail, which does not exist on this screen. The card is
              the shared `TemplateProposalExchange`; only this frame is Home's.
              Accept opens the created project (and the IDE shell it lives in)
              exactly as it does from the rail. */}
          {templateProposal ? (
            <TemplateProposalExchange
              proposal={templateProposal}
              chrome={({ children }) => (
                <div
                  data-testid="hero-template-proposal"
                  className="mx-4 mb-3 rounded-xl border border-[hsl(var(--brand-structure)/.25)] bg-[hsl(var(--brand-structure)/.05)] px-3.5 py-3"
                >
                  {children}
                </div>
              )}
              onDeclined={(declinedPrompt) => {
                // 자유 생성. Home's own entry into free generation, which is
                // its composer — armed with `confirmedIntakeRef` so the
                // router honours the decision instead of proposing the same
                // template again, then re-submitted. Identical mechanism to
                // an intake card's confirm, and it really does start
                // generation, which is what the card's own 자유 생성 copy
                // says is happening.
                //
                // The card is cleared rather than left in its `declined`
                // tone: that copy says generation is UNDERWAY, and on this
                // surface the composer's own progress box says the same thing
                // and keeps saying it — including when the run fails, where a
                // leftover "준비하는 중입니다" would sit next to the failure
                // alert contradicting it. (The rail keeps its declined bubble
                // because there the follow-up is a separate message, not the
                // same box.)
                setTemplateProposal(null)
                confirmedIntakeRef.current = { prompt: declinedPrompt }
                setPrompt(declinedPrompt)
                void handleSubmit(declinedPrompt)
              }}
            />
          ) : null}

          {clarificationQuestions.length > 0 ? (
            <div
              data-testid="hero-chat-clarification"
              className="mx-4 mb-3 rounded-xl border border-[#BAE6FD] bg-[#F0F9FF] px-3.5 py-3"
            >
              <div className="text-xs font-semibold text-[#0369A1]">
                {renewalCopy.clarificationLabel} · {clarificationQuestions.length}
                /3
              </div>
              <ol className="mt-2 space-y-1.5 text-sm leading-5 text-foreground">
                {clarificationQuestions.map((question, index) => (
                  <li key={`${index}:${question}`}>
                    <span className="mr-2 font-mono text-xs text-[#0284C7]">{index + 1}.</span>
                    {question}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          {libraryStructureJudgment ? (
            <div
              data-testid="hero-library-structure-cards"
              className="mx-4 mb-3 rounded-xl border border-[hsl(var(--brand-structure)/.25)] bg-[hsl(var(--brand-structure)/.05)] px-3.5 py-3"
            >
              <div className="text-xs font-semibold text-[hsl(var(--brand-structure))]">
                {libraryCopy.heading}
              </div>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {libraryCopy.detail}
              </p>
              <div className="mt-2 flex flex-col gap-1.5">
                {libraryStructureJudgment.candidates
                  .slice(0, 3)
                  .map((candidate) => (
                    <button
                      key={candidate.assetId}
                      type="button"
                      data-testid="hero-library-structure-candidate"
                      data-pattern-id={candidate.patternId}
                      data-asset-kind={candidate.kind}
                      onClick={() =>
                        resolveLibraryStructureJudgment({
                          kind: 'library-candidate',
                          candidate,
                        })
                      }
                      className="rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors hover:border-ring hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm font-medium text-foreground">
                          {candidate.title}
                        </span>
                        <span className="rounded border border-border bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {libraryCopy.kindLabels[candidate.kind]}
                        </span>
                        <span className="rounded border border-[#BBF7D0] bg-[#F0FDF4] px-1.5 py-0.5 text-[10px] font-medium text-[#15803D]">
                          {libraryCopy.admittedBadge}
                        </span>
                      </span>
                      {candidate.snippet ? (
                        <span className="mt-1 line-clamp-2 block text-xs text-muted-foreground">
                          {candidate.snippet}
                        </span>
                      ) : null}
                    </button>
                  ))}
                <button
                  type="button"
                  data-testid="hero-library-structure-start-fresh"
                  onClick={() =>
                    resolveLibraryStructureJudgment({ kind: 'start-fresh' })
                  }
                  className="rounded-lg border border-dashed border-border bg-background px-3 py-2 text-left transition-colors hover:border-ring hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="text-sm font-medium text-foreground">
                    {libraryCopy.startFresh}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {libraryCopy.startFreshDetail}
                  </span>
                </button>
                {/* G4 stub — fork-from-existing is wired by the next slice;
                    presented honestly as disabled, never as a dead click. */}
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  data-testid="hero-library-structure-import-existing"
                  className="cursor-not-allowed rounded-lg border border-dashed border-border bg-background px-3 py-2 text-left opacity-60"
                >
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium text-foreground">
                      {libraryCopy.importExisting}
                    </span>
                    <span className="rounded border border-border bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {libraryCopy.comingSoon}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          ) : null}

          {planningError ? (
            <div
              role="alert"
              data-testid="hero-chat-planning-error"
              className="mx-3 mb-2 flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive"
            >
              <span>{planningError}</span>
            </div>
          ) : planningMessage ? (
            <div
              role="status"
              data-testid="hero-chat-planning-progress"
              className="mx-3 mb-2 flex items-center gap-1 rounded-md border border-border/70 bg-background px-2 py-1 text-xs text-muted-foreground"
            >
              {isCreating ? (
                <LoaderCircle
                  className="h-3.5 w-3.5 animate-spin"
                  strokeWidth={2}
                />
              ) : null}
              <span className="truncate">{planningMessage}</span>
            </div>
          ) : null}

          {planningEvidence ? (
            <div
              data-testid="hero-chat-planning-evidence"
              data-planning-status={planningEvidence.status}
              role="status"
              className="mx-3 mb-2 flex flex-col gap-0.5 rounded-md border border-border/70 bg-background px-2 py-1.5 text-xs text-muted-foreground"
            >
              <span className="font-medium">{planningEvidence.title}</span>
              <span className="line-clamp-2">{planningEvidence.detail}</span>
            </div>
          ) : null}

          {/* Bottom bar */}
          <div className="flex items-start justify-between gap-2 px-4 pb-4 pt-1">
            <input
              ref={sourcePickerRef}
              type="file"
              hidden
              multiple
              accept={availableSourceExtensions.join(',')}
              onChange={handleSourcePick}
              data-testid="hero-source-picker"
            />
            <button
              type="button"
              onClick={() => sourcePickerRef.current?.click()}
              aria-label={t.attachLabel}
              className="flex h-10 items-center gap-2 rounded-full border border-[hsl(var(--brand-source-hairline))] bg-[hsl(var(--brand-source-surface))] px-3 text-xs font-semibold text-[hsl(var(--brand-source-strong))] transition-colors hover:bg-[hsl(var(--brand-source-surface-hover))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-source))]/30"
            >
              <Paperclip className="h-4 w-4" aria-hidden />
              {/* Advertise only intake that can succeed here: a build with no
                  reachable .fig importer (the appliance) drops "Figma" from
                  the chip instead of letting the reader pick a file and watch
                  the upload fail. */}
              {figmaImportAvailable
                ? renewalCopy.sourceAction
                : renewalCopy.sourceActionNoFigma}
            </button>
            {/* Submit */}
            {/* `data-testid` because this control's ONLY other handle is its
                accessible name, and that name is a dictionary entry
                (`t.send` / `t.cancel`) — five locales, two states. A harness
                that reaches it by name is pinned to one language, which is
                exactly what blocked the whole journey from running under `ko`
                (slice 2, Task 1). The testid is stable across both the
                send/cancel state swap and every locale; the aria-label stays
                as the USER-facing name and is unchanged. */}
            {/* The primary control NEVER cancels. It used to: same position,
                same size, icon and name swapping while a request ran -- so a
                person who pressed send, saw nothing, and pressed again
                cancelled their own request, which reads as "sending does
                nothing" (measured 2026-08-24). Cancel is now its own control,
                offered only while there is something to cancel. */}
            {composerAction.cancelVisible ? (
              <button
                data-testid="overview-chat-cancel"
                onClick={handleCancel}
                aria-label={t.cancel}
                className="flex h-10 shrink-0 items-center gap-1 rounded-[11px] border border-border px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
              >
                <X className="h-3.5 w-3.5" />
                {t.cancel}
              </button>
            ) : null}
            <button
              data-testid="overview-chat-send"
              onClick={composerAction.primaryAction === "send" ? handleSubmit : undefined}
              disabled={composerAction.primaryDisabled}
              aria-label={t.send}
              aria-busy={isCreating || undefined}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-[#0EA5E9] text-white transition-colors hover:bg-[#0284C7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5E9]/30 disabled:cursor-not-allowed disabled:opacity-30"
            >
              {isCreating ? (
                <LoaderCircle className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowUp className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
